import React from 'react';
import TextAreaField from '../shared/TextAreaField';
import CheckboxField from '../shared/CheckboxField';

const EventDescription = ({ 
  formData, 
  updateFormData, 
  errors = {},
  loading = false 
}) => {
  const handleFieldChange = (updates) => {
    updateFormData(updates);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-full">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Event Description</h2>
            <p className="text-sm text-gray-600 mt-1">
              Provide a detailed description of what happened. Be as specific as possible.
            </p>
          </div>
        </div>
      </div>

      {/* Main Event Description */}
      <TextAreaField
        label="Detailed Description of Event"
        name="event_description"
        required
        rows={8}
        value={formData.event_description}
        onChange={handleFieldChange}
        error={errors.event_description}
        placeholder="Provide a clear, detailed description of what happened, including:
• Sequence of events leading up to the incident
• What exactly occurred during the event
• Environmental conditions at the time
• People involved and their actions
• Equipment or systems involved
• Any contributing factors you observed

Example: 'While performing routine maintenance on aircraft HS-ABC in Hangar 1, I noticed hydraulic fluid leaking from the left main landing gear. Upon closer inspection, I found that the hydraulic line connection was loose. When I attempted to tighten it with the appropriate torque wrench, the fitting cracked, causing a significant fluid leak onto the hangar floor...'"
        maxLength={2000}
        showWordCount={true}
        helpText="Be specific and factual. Describe what you saw, heard, or experienced. Avoid speculation about causes."
        disabled={loading}
      />

      {/* Immediate Actions Taken */}
      <TextAreaField
        label="Immediate Actions Taken"
        name="immediate_actions_taken"
        rows={6}
        value={formData.immediate_actions_taken}
        onChange={handleFieldChange}
        error={errors.immediate_actions_taken}
        placeholder="Describe what immediate actions were taken to address the situation:
• Safety measures implemented
• Equipment shut down or secured
• Personnel evacuated or notified
• Cleanup or containment actions
• Notifications made to supervisors or management
• Work stopped or area cordoned off

Example: 'Immediately stopped work and secured the aircraft. Placed absorbent materials around the leak to prevent spreading. Notified shift supervisor and maintenance control. Posted warning signs around the affected area. Completed incident notification form and began replacement of damaged hydraulic line...'"
        maxLength={1000}
        showWordCount={true}
        helpText="What did you or others do immediately after the event occurred?"
        disabled={loading}
      />

      {/* Potential Consequences */}
      <TextAreaField
        label="Potential Consequences"
        name="potential_consequences"
        rows={4}
        value={formData.potential_consequences}
        onChange={handleFieldChange}
        error={errors.potential_consequences}
        placeholder="Describe what could have happened or potential outcomes:
• What worse outcomes were avoided
• What could happen if similar events occur
• Potential impact on safety, operations, or environment
• Lessons learned from this event

Example: 'If the hydraulic leak had not been detected, it could have resulted in landing gear failure during aircraft operation. The leak could have also created a slip hazard for personnel working in the area. Early detection prevented potential aircraft damage and personnel injury...'"
        maxLength={1000}
        showWordCount={true}
        helpText="What could have happened? What were the potential outcomes if things had gone differently?"
        disabled={loading}
      />

      {/* Near Miss Section */}
      <div className="border-t border-gray-100 pt-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg className="flex-shrink-0 w-5 h-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-900">Near Miss Information</h4>
              <p className="text-sm text-amber-700 mt-1 mb-4">
                A near miss is an event that could have resulted in injury, damage, or other adverse consequences but didn't.
              </p>

              <CheckboxField
                name="is_near_miss"
                label="This was a near miss event"
                checked={formData.is_near_miss}
                onChange={handleFieldChange}
                description="Check if this event had the potential for adverse consequences but none actually occurred"
                disabled={loading}
              />

              {formData.is_near_miss && (
                <div className="mt-4">
                  <TextAreaField
                    label="Near Miss Description"
                    name="near_miss_description"
                    rows={4}
                    value={formData.near_miss_description}
                    onChange={handleFieldChange}
                    error={errors.near_miss_description}
                    placeholder="Describe the potential consequences that were avoided:
• What injury or damage could have occurred
• Why the adverse consequences didn't happen
• What prevented the worst-case scenario
• How close was this to becoming an actual incident

Example: 'This was a near miss because if the hydraulic fluid had not been noticed, the aircraft could have been released to service with a compromised landing gear system. The leak was discovered during routine inspection before the aircraft was returned to operation, preventing potential in-flight emergency or landing gear failure...'"
                    maxLength={1000}
                    showWordCount={true}
                    className="mt-4"
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Assistance Placeholder */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
          <h4 className="text-sm font-medium text-blue-900">AI Writing Assistant</h4>
        </div>
        <p className="text-sm text-blue-700 mt-1">
          As you type your event description, our AI assistant will help suggest relevant categories, identify potential safety concerns, and recommend related information to include in your report.
        </p>
        <div className="mt-2 text-xs text-blue-600">
          <span className="inline-flex items-center">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="3"/>
            </svg>
            AI suggestions will appear here based on your description
          </span>
        </div>
      </div>

      {/* Description Guidelines */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Writing Tips for Effective Safety Reports</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <h5 className="font-medium text-gray-900 mb-2">✓ Do:</h5>
            <ul className="space-y-1">
              <li>• Use clear, factual language</li>
              <li>• Include specific times, locations, and people</li>
              <li>• Describe the sequence of events</li>
              <li>• Mention relevant procedures or standards</li>
              <li>• Include environmental conditions</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-900 mb-2">✗ Avoid:</h5>
            <ul className="space-y-1">
              <li>• Speculation about causes</li>
              <li>• Blaming individuals</li>
              <li>• Vague or general statements</li>
              <li>• Technical jargon without explanation</li>
              <li>• Assumptions about intent</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-sm text-gray-600">Processing description...</span>
        </div>
      )}
    </div>
  );
};

export default EventDescription;
